import React from 'react';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import QuestionPanel from './BCPQuestionPanel';
import DocumentPreview from './BCPDocumentPreview';
import ProgressBar from './BCPProgressBar';
import { bcpQuestionnaireSchema } from './bcp-questionnaire-schema';
import { generateBCPDocument } from './bcp-template-generator';
import BCPSummaryPage from './BCPSummaryPage';
import BCPWelcome from './BCPWelcome';
import SuccessionThemeProvider, { useSuccessionTheme } from './SuccessionThemeProvider';
import { 
  CheckCircleIcon, 
  ChartBarIcon, 
  UserGroupIcon, 
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon 
} from '@heroicons/react/24/outline';

// GraphQL mutation to save BCP form data
const SAVE_BCP_DATA = gql`
  mutation SaveBcpFormData(
    $formData: String!
    $currentSection: Int!
    $sectionCount: Int!
    $status: String
    $welcomeShown: Boolean
  ) {
    saveBcpFormData(
      formData: $formData
      currentSection: $currentSection
      sectionCount: $sectionCount
      status: $status
      welcomeShown: $welcomeShown
    ) {
      bcpFormDatum {
        id
        user_id
        formData
        currentSection
        sectionCount
        completionPercentage
        status
        welcomeShown
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

// GraphQL mutation to finalize BCP and generate AI insights
const FINALIZE_BCP = gql`
  mutation FinalizeBcp {
    finalizeBcp {
      bcpFormDatum {
        id
        user_id
        formData
        currentSection
        sectionCount
        completionPercentage
        status
        aiInsights {
          key_strengths
          identified_risks
          action_recommendations
          compliance_status
        }
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

// Inner component that uses the theme context
const BCPGeneratorContent = ({ bcpData }) => {
  const { theme, isLightTheme, isDarkTheme, company, getCSSVariable } = useSuccessionTheme();

  return (
    <BCPGeneratorClass 
      bcpData={bcpData}
      theme={theme}
      isLightTheme={isLightTheme}
      isDarkTheme={isDarkTheme}
    />
  );
};

class BCPGeneratorClass extends React.Component {
  constructor(props) {
    super(props);
    const { bcpData } = props;
    
    // Debug logging for BCP data
    console.log("BCPGenerator initialized with bcpData:", bcpData);
    console.log("BCPGenerator aiInsights from bcpData:", bcpData?.aiInsights);
    
    let initialFormData = {};
    let initialSection = 0;
    
    // If we have BCP data, initialize state from it
    if (bcpData && bcpData.formData) {
      try {
        initialFormData = JSON.parse(bcpData.formData);
        initialSection = bcpData.currentSection || 0;
      } catch (error) {
        console.error('Error parsing BCP form data:', error);
      }
    }
    
    // Determine if we should show the summary page immediately based on completion percentage
    // Only show summary if BCP is actually 100% complete AND has AI insights
    const completionPercentage = bcpData?.completionPercentage || 0;
    const hasAiInsights = bcpData?.aiInsights && Object.keys(bcpData.aiInsights).length > 0;
    const isCompleted = completionPercentage === 100 && hasAiInsights;
    
    // Determine if this is a new user with not_started status and welcome not shown
    const isNewUser = !bcpData || bcpData.status === 'not_started';
    const welcomeShown = bcpData?.welcomeShown || false;
    
    this.state = {
      currentSection: initialSection,
      formData: initialFormData,
      previewContent: '',
      loading: false,
      error: null,
      showingSummary: isCompleted, // Show summary page immediately if BCP is completed
      aiInsights: bcpData?.aiInsights || null,
      showingWelcomeScreen: isNewUser && !welcomeShown,
      welcomeShown: welcomeShown
    };
    
    // Debug logging for state initialization
    console.log("BCPGenerator initial state aiInsights:", this.state.aiInsights);
  }

  componentDidMount() {
    const { bcpData } = this.props;
    // Initial document preview
    this.updatePreview(this.state.formData);
    
    // Check if we need to update the AI insights from the props
    if (bcpData?.aiInsights && !this.state.aiInsights) {
      console.log("Updating aiInsights from bcpData in componentDidMount");
      this.setState({ aiInsights: bcpData.aiInsights });
    }
    
    // Generate document preview immediately if we're showing summary
    if (this.state.showingSummary && !this.state.previewContent) {
      const previewContent = generateBCPDocument(this.state.formData);
      this.setState({ previewContent });
    }
  }

  componentDidUpdate(prevProps) {
    // Hydrate state.formData if props.bcpData.formData arrives/changes and local state is empty
    const prevFormDataStr = prevProps?.bcpData?.formData;
    const nextFormDataStr = this.props?.bcpData?.formData;

    const stateIsEmpty = !this.state.formData || Object.keys(this.state.formData).length === 0;
    const incomingChanged = nextFormDataStr && nextFormDataStr !== prevFormDataStr;

    if (incomingChanged && stateIsEmpty) {
      try {
        const parsed = JSON.parse(nextFormDataStr);
        const nextSection = this.props?.bcpData?.currentSection ?? this.state.currentSection ?? 0;
        const previewContent = generateBCPDocument(parsed);
        this.setState({ formData: parsed, currentSection: nextSection, previewContent });
      } catch (e) {
        console.error('Error parsing incoming BCP form data in componentDidUpdate:', e);
      }
    }
  }

  updatePreview = (formData) => {
    const previewContent = generateBCPDocument(formData);
    this.setState({ previewContent });
  };

  handleSectionChange = (direction, saveBcpFormData) => {
    this.setState(prevState => {
      // Calculate the new section index
      const newSection = prevState.currentSection + direction;
      
      // Save current data before changing sections
      this.saveBcpData(saveBcpFormData, prevState.formData, newSection);
      
      return {
        currentSection: newSection
      };
    });
  };

  handleFormDataChange = (newSectionData, saveBcpFormData) => {
    const { formData, currentSection } = this.state;
    const section = bcpQuestionnaireSchema.sections[currentSection];
    
    // Merge the new data with existing form data
    const mergedData = {
      ...formData,
      [section.id]: newSectionData
    };
    
    // Update state with the new form data and generate new preview content
    this.setState(prevState => {
      // Generate new preview
      const previewContent = generateBCPDocument(mergedData);
      
      // Save data to the database through GraphQL
      this.saveBcpData(saveBcpFormData, mergedData, prevState.currentSection);
      
      return {
        formData: mergedData,
        previewContent,
      };
    });
  };
  
  saveBcpData = (saveBcpFormData, formData, currentSection) => {
    const sectionCount = bcpQuestionnaireSchema.sections.length;
    
    // Determine if the form is complete by checking if all required sections have data
    let isComplete = true;
    let filledSections = 0;
    
    // Check each section
    bcpQuestionnaireSchema.sections.forEach(section => {
      const sectionData = formData[section.id];
      // A section is considered filled if it has any data
      const isSectionFilled = sectionData && Object.keys(sectionData).length > 0;
      
      if (isSectionFilled) {
        filledSections++;
      } else {
        // Only mark as incomplete if this is a required section
        if (!section.optional) {
          isComplete = false;
        }
      }
    });
    
    // Calculate completion percentage (capped at 100%)
    const completionPercentage = Math.min(100, Math.round((filledSections / sectionCount) * 100));
    
    // Determine status
    const status = isComplete ? 'completed' : filledSections > 0 ? 'in_progress' : 'not_started';
    
    // Save BCP form data
    saveBcpFormData({
      variables: {
        formData: JSON.stringify(formData),
        currentSection: currentSection,
        sectionCount: sectionCount,
        status: status,
        welcomeShown: this.state.welcomeShown
      },
    }).then(({ data }) => {
      // Handle successful save
      if (data.saveBcpFormData.errors.length > 0) {
        console.error('Error saving BCP form data:', data.saveBcpFormData.errors);
      }
    }).catch((error) => {
      console.error('Error saving BCP form data:', error);
    });
  };

  handleFinalizeClick = (finalizeBcp) => {
    this.setState({ loading: true });
    
    // Call the finalize mutation to generate AI insights
    finalizeBcp()
      .then(({ data }) => {
        if (data.finalizeBcp.errors && data.finalizeBcp.errors.length > 0) {
          console.error('Error finalizing BCP:', data.finalizeBcp.errors);
          this.setState({ 
            error: `Error finalizing BCP: ${data.finalizeBcp.errors.join(', ')}`,
            loading: false 
          });
        } else {
          // Successfully finalized and generated insights
          const bcpFormDatum = data.finalizeBcp.bcpFormDatum;
          
          this.setState({ 
            aiInsights: bcpFormDatum.aiInsights,
            loading: false,
            showingSummary: true
          });
        }
      })
      .catch((error) => {
        console.error('Error finalizing BCP:', error);
        this.setState({ 
          error: `Error finalizing BCP: ${error.message}`,
          loading: false
        });
      });
  }

  handleBackToEditor = () => {
    // When going back to editor from summary, we need to ensure the formData
    // contains all the complete BCP data, not just what's currently in state
    const { bcpData } = this.props;
    
    let completeFormData = this.state.formData;
    
    // If we have bcpData with formData, use that as the complete data source
    if (bcpData && bcpData.formData) {
      try {
        completeFormData = JSON.parse(bcpData.formData);
        console.log("Restored complete form data from bcpData:", completeFormData);
      } catch (error) {
        console.error('Error parsing BCP form data in handleBackToEditor:', error);
      }
    }
    
    this.setState({ 
      showingSummary: false,
      formData: completeFormData 
    });
  }

  handleStartBcp = (saveBcpFormData) => {
    // Update local state first
    this.setState({ showingWelcomeScreen: false, welcomeShown: true });
    
    // Create minimal form data for new users
    const formData = JSON.stringify({});
    
    // Save the welcome shown state to the backend
    saveBcpFormData({
      variables: {
        formData: formData,
        currentSection: 0,
        sectionCount: bcpQuestionnaireSchema.sections.length,
        status: 'not_started',
        welcomeShown: true
      },
    }).catch((error) => {
      console.error('Error saving welcome shown status:', error);
    });
  };

  render() {
    const { sections } = bcpQuestionnaireSchema;
    const { 
      currentSection, formData, previewContent, loading, error, 
      showingSummary, aiInsights, showingWelcomeScreen 
    } = this.state;
    const { bcpData } = this.props;
    const isCompleted = bcpData?.status === 'completed';
    

    
    return (
      <Mutation mutation={FINALIZE_BCP}>
        {(finalizeBcp, { loading: finalizeLoading }) => (
          <Mutation mutation={SAVE_BCP_DATA}>
            {(saveBcpFormData, { loading: mutationLoading }) => {
              // Update the loading state
              const isLoading = loading || mutationLoading || finalizeLoading;
              
              // Show welcome screen for new users
              if (showingWelcomeScreen) {
                return <BCPWelcome onStartClick={() => this.handleStartBcp(saveBcpFormData)} />;
              }
              
              // If showing summary, render the summary page
              if (showingSummary) {
                console.log("BCPGenerator rendering summary page with aiInsights:", aiInsights);
                return (
                  <BCPSummaryPage 
                    formData={formData} 
                    previewContent={previewContent}
                    onRevise={this.handleBackToEditor}
                    aiInsights={aiInsights}
                  />
                );
              }
              
              // Get current section
              const section = sections[currentSection];
              if (!section) return <div>Loading...</div>;
              
              return (
                <div className="flex h-screen overflow-hidden bg-succession-bg-secondary transition-colors duration-300">
                  
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <main className="flex-1 relative overflow-y-auto focus:outline-none">
                      <div className="py-6">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                          <h1 className="text-2xl font-semibold text-succession-text-primary transition-colors duration-300">
                            Business Continuity Plan Generator
                          </h1>
                          
                          {isCompleted && (
                            <div className="mt-6 mb-4 flex items-center py-3 px-4 rounded-md bg-succession-primary-10 border border-succession-primary transition-colors duration-300">
                              <span className="text-sm text-succession-primary transition-colors duration-300">
                                Your BCP is already complete. You can make edits here or{' '}
                                <button 
                                  type="button" 
                                  className="underline font-medium text-succession-primary hover:opacity-80 transition-all duration-300" 
                                  onClick={() => this.handleFinalizeClick(finalizeBcp)}
                                >
                                  return to the summary page
                                </button>.
                              </span>
                            </div>
                          )}
                          
                          {error && (
                            <div className="mt-4 p-4 rounded-md bg-succession-error-10 border border-succession-error transition-colors duration-300">
                              <div className="flex">
                                <div className="ml-3">
                                  <h3 className="text-sm font-medium text-succession-error transition-colors duration-300">
                                    Error saving data
                                  </h3>
                                  <div className="mt-2 text-sm transition-colors duration-300">
                                    <p className="text-succession-error">{error}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-6">
                            <ProgressBar 
                              currentSection={currentSection}
                              totalSections={sections.length}
                              sectionTitles={sections.map(s => s.title)}
                            />
                          </div>
                          
                          {isCompleted && (
                            <div className="flex justify-end items-center mt-4 mb-2 py-3 px-4 rounded-md bg-succession-success-10 border border-succession-success transition-colors duration-300">
                              <CheckCircleIcon className="h-5 w-5 mr-2 text-succession-success transition-colors duration-300" />
                              <span className="text-sm font-medium mr-4 text-succession-success transition-colors duration-300">
                                Your BCP is complete!
                              </span>
                              <button
                                type="button"
                                onClick={() => this.handleFinalizeClick(finalizeBcp)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-success hover:opacity-90 transition-all duration-300"
                              >
                                Finalize BCP
                              </button>
                            </div>
                          )}
                          
                          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
                              <QuestionPanel
                                section={section}
                                formData={formData}
                                onFormDataChange={(newData) => this.handleFormDataChange(newData, saveBcpFormData)}
                                onNext={() => this.handleSectionChange(1, saveBcpFormData)}
                                onPrevious={() => this.handleSectionChange(-1, saveBcpFormData)}
                                onFinalize={() => this.handleFinalizeClick(finalizeBcp)}
                                isFirstSection={currentSection === 0}
                                isLastSection={currentSection === sections.length - 1}
                              />
                            </div>
                            
                            <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
                              <DocumentPreview 
                                content={previewContent} 
                              />
                            </div>
                          </div>
                          
                          {isLoading && (
                            <div className="fixed bottom-4 right-4 text-white py-2 px-4 rounded-md bg-succession-primary shadow-succession-md transition-colors duration-300">
                              Saving...
                            </div>
                          )}
                        </div>
                      </div>
                    </main>
                  </div>
                </div>
              );
            }}
          </Mutation>
        )}
      </Mutation>
    );
  }
}

// Main component wrapper with theme provider
const BCPGenerator = ({ company, current_user, ...props }) => {
  return (
    <SuccessionThemeProvider company={company} user={current_user}>
      <BCPGeneratorContent {...props} />
    </SuccessionThemeProvider>
  );
};

export default BCPGenerator; 